import { Box, Collapse } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useCallback, useEffect, useState } from 'react';
import Button from '@ui/Button';
import Typography from '@ui/Typography';
import Icon from '@ui/Icon';

interface CollapsibleRowProps {
  id: string;
  title: string;
  children: React.ReactNode;
  onOpen?: (open: boolean) => void;
  disabled?: boolean;
  defaultOpen?: boolean;
}

const useStyles = makeStyles<{ open: boolean }>()((theme, { open }) => ({
  rowContainer: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  },
  iconButton: {
    marginLeft: 'auto',
    borderRadius: theme.spacing(0.5),
    '&:hover, &.Mui-focusVisible': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:disabled': {
      cursor: 'not-allowed',
    },
  },
  icon: {
    fontSize: `${theme.spacing(2.5)} !important`,
  },
  plusIcon: {
    position: 'absolute',
    opacity: !open ? 1 : 0,
    transition: theme.transitions.create('opacity', { duration: 500 }),
  },
  content: {
    opacity: open ? 1 : 0,
    transition: theme.transitions.create('opacity', { duration: 500 }),
  },
  disabled: {
    color: theme.palette.grey[200],
    cursor: 'not-allowed',
  },
}));

/**
 * Row with title text and icon button to show/hide content
 * Content fades in and out upon collapsing/expanding
 */
const CollapsibleRow = (props: CollapsibleRowProps) => {
  const { id, children, title, onOpen, disabled, defaultOpen } = props;
  const [open, setOpen] = useState(false);
  const { classes, cx } = useStyles({ open });
  const regionId = `${id}-collapsible-region`;
  const handleToggle = useCallback(() => {
    setOpen(!open);
    if (onOpen) onOpen(!open);
  }, [open, onOpen]);

  useEffect(() => {
    if (defaultOpen) setOpen(defaultOpen);
  }, [defaultOpen]);

  return (
    <Box>
      <Box
        id={id}
        className={cx(classes.rowContainer, {
          [classes.disabled]: disabled,
        })}
        onClick={disabled ? () => {} : handleToggle}
        aria-label={title}
        aria-controls={regionId}
      >
        <Typography
          variant="h5"
          bold
          color={disabled ? 'gray_200' : 'primary'}
          component="h3"
        >
          {title}
        </Typography>
        <Button
          variant="icon"
          aria-label={title}
          aria-expanded={open}
          className={classes.iconButton}
          onClick={handleToggle}
          disabled={disabled}
        >
          <Box display={'flex'}>
            <Icon
              className={cx(classes.icon, classes.plusIcon, {
                [classes.disabled]: disabled,
              })}
              type={'plus'}
            />
            <Icon className={classes.icon} type={'minus'} />
          </Box>
        </Button>
      </Box>
      <Box id={regionId} role="region" aria-labelledby={id}>
        <Collapse in={open} timeout={500}>
          <Box className={classes.content} mt={3}>
            {children}
          </Box>
        </Collapse>
      </Box>
    </Box>
  );
};

export default CollapsibleRow;
